// third party imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// internal rebet imports
import "./stylesCasinoBetsTable.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";
import { formatLargeNumbers } from "../../../../utils/NumberFormatter";

// component imports
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";

// asset imports

import { Input, Table, Pagination } from "antd";
import {
  BET_STATUS,
  BET_TYPES,
  KPI_CARD_BACKGROUND,
} from "../../../../utils/Constants";
import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";

import { SearchOutlined } from "@ant-design/icons";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

const BetTable = ({ userData }) => {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );
  const navigate = useNavigate();

  // state variables
  const [searchValue, setSearchValue] = useState("");
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState({});
  const [betsList, setBetsList] = useState([]);
  const [betsToShowInTable, setBetsToShowInTable] = useState([]);
  const [isApiRunning, setIsApiRunning] = useState(false);
  const [resetTable, setResetTable] = useState(false);
  const todayRange = [dayjs().startOf("day"), dayjs().endOf("day")];
  const [casinoBetsDates, setCasinoBetsDates] = useState(null);
  const [summary, setSummary] = useState({
    total_wager: 0,
    total_payout: 0,
  });
  const [betsCache, setBetsCache] = useState({});

  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  // use effects
  useEffect(() => {
    getUsersCasinoBets(1, true, todayRange, false, true);
  }, [userData]);

  // api functions
  const getUsersCasinoBets = async (
    page = 1,
    refresh = false,
    queryString = todayRange,
    isQueryString = true,
    allTime = false
  ) => {
    setIsApiRunning(true);

    let startDate = "";
    let endDate = "";

    if (isQueryString) {
      let formattedQueryString = queryString.split("&");
      startDate = dayjs(formattedQueryString[0]).format("YYYY-MM-DD");
      endDate = dayjs(formattedQueryString[1]).format("YYYY-MM-DD");
    } else if (allTime) {
      startDate = null;
      endDate = null;
    } else {
      startDate = queryString[0]?.format("YYYY-MM-DD");
      endDate = queryString[1]?.format("YYYY-MM-DD");
    }

    const cacheKey = page;

    if (betsCache[cacheKey] && !refresh) {
      const cached = betsCache[cacheKey];
      setBetsToShowInTable(cached.all_bets);
      setPagination(cached.pagination);
      setIsApiRunning(false);
      return;
    }

    let params = {
      page: page,
      user_id: userData?.user_id,
      start_date: startDate,
      end_date: endDate,
    };


    if (!refresh) {
      params.last_evaluated_key = lastEvaluatedKey;
    }

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_USER_CASINO_BETS,
        params
      );
      const all_bets = response?.data?.data?.transactions.map((bet) => {
        return {
          ...bet,
          wager: bet?.title === "bet_won" ? 0 : handleBetWager2(bet),
          payout: handleBetPayout2(bet),
        };
      });

      const summary = response?.data?.data?.totals;
      setSummary({
        total_wager: summary?.total_wagers,
        total_payout: summary?.total_payouts,
      });
      const lastEvaluatedKey = response?.data?.data?.last_evaluated_key;

      const newUniqueBets = all_bets.filter((newBet) => {
        return !betsList.some((existing) => existing.bid === newBet.bid);
      });

      const paginationData = {
        current_page: response?.data?.data?.pagination?.current_page,
        page_size: response?.data?.data?.pagination?.page_size,
        total_results: response?.data?.data?.pagination?.total_results,
        total_pages: response?.data?.data?.pagination?.total_pages,
        has_next: response?.data?.data?.pagination?.has_next,
        has_prev: response?.data?.data?.pagination?.has_prev,
      };

      console.log("paginationData", paginationData);
      setPagination(paginationData);

      if (refresh) {
        setBetsList(all_bets);
        setBetsToShowInTable(all_bets);
      } else {
        setBetsList((prev) => [...prev, ...newUniqueBets]);
        setBetsToShowInTable(newUniqueBets);
      }

      setBetsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          all_bets,
          summary,
          pagination: paginationData,
        },
      }));

      setSearchValue("");
      setResetTable(false);
      setLastEvaluatedKey(lastEvaluatedKey);
      setIsApiRunning(false);
    } catch (error) {
      console.log("error fetching users bets", error?.response);
      setIsApiRunning(false);
      handleExpiredTokenError(navigate, error?.response);
    }
  };

  const searchBet = async () => {
    setIsApiRunning(true);

    const params = {
      bet_id: searchValue,
      bet_type: 6,
    };

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_BET_BY_ID,
        params
      );

      setBetsToShowInTable([response?.data?.data]);
      setResetTable(true);
      setIsApiRunning(false);
    } catch (error) {
      console.log("error searching for bet", error?.response);
      setIsApiRunning(false);
      handleExpiredTokenError(navigate, error?.response);
    }
  };

  // helper functions
  const handleSearchValueChange = (event) => {
    setSearchValue(event?.target?.value);
  };

  const handleSearchPressed = () => {
    searchBet();
  };

  const formatTimestamp = (timestampStr) => {
    dayjs.extend(utc);

    const timestamp = Number(timestampStr);
    return dayjs.unix(timestamp).utc().format("DD MMMM YYYY, HH:mm:ss");
  };

  const handleBetWager = (bet_data) => {
    if (bet_data?.title === "bet_lose" && bet_data?.amount === "0") return "-";
    return formatLargeNumbers(bet_data?.amount, 2);
  };

  const handleBetWager2 = (bet_data) => {
    if (bet_data?.title === "bet_lose" && bet_data?.amount === "0") return 0;
    return parseFloat(bet_data?.amount)?.toFixed(2);
  };

  const handleBetPayout2 = (bet_data) => {
    if (bet_data?.title === "bet_lose" && bet_data?.amount === 0) return 0;
    return bet_data?.title === "bet_won"
      ? parseFloat(bet_data?.amount)?.toFixed(2)
      : bet_data?.title === "bet_placement"
      ? 0
      : 0;
  };

  const handleBetTitle = (title) => {
    if (title === "bet_won") {
      return "WON";
    } else if (title === "bet_lose") {
      return "LOST";
    } else if (title === "bet_placement") {
      return "PLACED";
    } else {
      return "-";
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const convertDecimalToAmerican = (decimalOdds) => {
    let americanOdds;

    if (decimalOdds > 2.0) {
      americanOdds = "+" + Math.round((decimalOdds - 1) * 100);
    } else if (decimalOdds < 2.0) {
      americanOdds = Math.round(-(100 / (1 - decimalOdds)));
      americanOdds =
        americanOdds > 0 ? "-" + americanOdds : americanOdds.toString();
    } else {
      americanOdds = "+100";
    }

    return americanOdds;
  };

  const columns = [
    {
      title: "Date / Time",
      dataIndex: "ts",
      key: "ts",
      width: 200,
      render: (text, record) => formatTimestamp(record?.ts),
    },
    {
      title: "ID",
      dataIndex: "bid",
      key: "bid",
      width: 250,
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      width: 150,
      render: (text, record) => {
        return record?.sub ? record?.sub : record?.provider;
      },
    },
    {
      title: "Game",
      dataIndex: "game",
      key: "game",
      width: 150,
      render: (text, record) => {
        return record?.game ? record?.game : "-";
      },
    },
    {
      title: "Wager",
      dataIndex: "money_type",
      key: "money_type",
      width: 150,
      sorter: (a, b) => a?.wager - b?.wager,
      render: (text, record) => {
        return record?.title === "bet_won" ? "-" : handleBetWager(record);
      },
    },
    {
      title: "Payout",
      dataIndex: "payout",
      key: "payout",
      width: 150,
      sorter: (a, b) => a?.payout - b?.payout,
      render: (text, record) => {
        if (record?.title === "bet_lose" && record?.amount === 0) return "-";
        return record?.title === "bet_won"
          ? formatLargeNumbers(record?.amount, 2)
          : record?.title === "bet_placement"
          ? "-"
          : "0.00";
      },
    },
    {
      title: "Status",
      dataIndex: "title",
      key: "title",
      width: 200,
      render: (text, record) => {
        return (
          <td className={`casino-bet-bg ${record.title}`}>
            <td className={`casino-bet-text ${record.title}`}>
              {handleBetTitle(record.title)}
            </td>
          </td>
        );
      },
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      width: 100,
      render: (text) => {
        return formatLargeNumbers(text, 2);
      },
    },
  ];

  const handleDateChange = (queryString) => {
    getUsersCasinoBets(1, true, queryString, true, false);
  };

  const handleResetTable = () => {
    setCasinoBetsDates(null);
    getUsersCasinoBets(1, true, todayRange, false, true);
  };

  const handlePaginationChange = (page) => {
    let allTimeParams = casinoBetsDates ? false : true;
    let queryStringParams = casinoBetsDates ? casinoBetsDates : todayRange;

    getUsersCasinoBets(page, false, queryStringParams, false, allTimeParams);
  };

  return (
    <div className="casino-bets-container">
      <div className="casino-bets-header">
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={isApiRunning}
            title="Total Wager"
            value={summary?.total_wager}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title="Total Payout"
            value={summary?.total_payout}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={casinoBetsDates}
          setDates={setCasinoBetsDates}
          showPresets={true}
          startRange="Today"
        />
      </div>
      <div className="casino-bets-table-container">
        <div className="head-controls casino-bets-table-header-container">
          <div className="header-text-container">
            <p className="header-text">User's Casino Bets</p>
          </div>

          <Input
            type="text"
            placeholder="Search by ID"
            className="bet-searchInput"
            value={searchValue}
            onChange={handleSearchValueChange}
          />

          <button className="search-button" onClick={handleSearchPressed}>
            <SearchOutlined style={{ fontSize: "20px", color: "white" }} />
          </button>
        </div>

        <div className="">
          {isApiRunning ? (
            <RebetLoader height={"100px"} width={"100px"} />
          ) : (
            <div className="casino-bets-table">
              <Table
                dataSource={betsToShowInTable}
                columns={columns}
                scroll={{ x: 800, y: 500 }}
                pagination={false}
              />
              {resetTable ? (
                <div className="reset-table-button-container">
                  <SaveButton
                    buttonText="Reset Table"
                    className="reset-table-button"
                    callbackFunction={handleResetTable}
                    isLoading={isApiRunning}
                  />
                </div>
              ) : (
                !isApiRunning &&
                pagination?.total_results > 10 && (
                  <div className="sports-bets-pagination-container">
                    <Pagination
                      current={pagination.current_page}
                      total={pagination.total_results}
                      pageSize={pagination.page_size}
                      showSizeChanger={false}
                      onChange={(page) => {
                        if (
                          page === pagination.current_page - 1 ||
                          page === pagination.current_page + 1
                        ) {
                          handlePaginationChange(page);
                        }
                      }}
                      itemRender={(page, type, originalElement) => {
                        if (type === "page") {
                          return (
                            <a
                              rel="nofollow"
                              disabled={page > pagination.current_page + 1}
                              className={`${
                                page > pagination.current_page + 1
                                  ? "disabled"
                                  : ""
                              }`}
                            >
                              {page}
                            </a>
                          );
                        }
                        return originalElement;
                      }}
                    />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BetTable;
